'use client';

import { useId } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Position, WISHLIST_MODAL_ID } from '@headless-workspace/config';
import { SideMenuModal, useTailwindBreakpoint, useUIContext } from '@headless-workspace/core-ui';
import {
    GlowCarousel,
    GlowCarouselProps,
    GlowProductTile,
    ModalCommonClasses,
    PropsWithClassName,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { DataLayerClassName, useProductTilesMapper, useWishlist, WishlistModal } from '../../../client';
import { TileProductValue } from '../../../server';

type ProductCarouselProps = PropsWithClassName &
    PropsWithLocale &
    Pick<GlowCarouselProps, 'navigationButtonClassName' | 'removeMarginAtViewport'> & {
        products: TileProductValue[];
        wishlistBtnAriaLabel: string;
        enablePriceExceptionLegalMentions: boolean;
    };

export const HomeProductCarousel: Component<ProductCarouselProps> = ({
    className,
    products,
    wishlistBtnAriaLabel,
    navigationButtonClassName,
    locale,
    enablePriceExceptionLegalMentions,
    removeMarginAtViewport,
}) => {
    const isTablet = useTailwindBreakpoint('tablet');
    const {
        menuModal: { addIfAbsentAndOpen },
    } = useUIContext();
    const { shouldAddToWishlist, update: updateWishlist, isProductInWishlist } = useWishlist();
    const productTileId = useId();
    const productTiles = useProductTilesMapper({
        products,
        locale,
        showStockStatusBtn: false,
        enablePriceExceptionLegalMentions,
    });
    const t = useTranslations('Discover.carousel.accessibility');

    return (
        <GlowCarousel
            ariaLabels={{
                carousel: t('carousel'),
                next: t('nextBtn'),
                prev: t('prevBtn'),
                product: t('product'),
                productList: t('productList'),
                progressBar: t('progressBar'),
            }}
            className={className}
            productTileId={productTileId}
            navigationButtonClassName={navigationButtonClassName}
            removeMarginAtViewport={removeMarginAtViewport}
        >
            {productTiles.map((product) => {
                return (
                    <GlowProductTile
                        key={product.id}
                        {...product}
                        isTablet={isTablet}
                        ImageTagName={Image}
                        dataLayer={{
                            id: product.trackingId,
                            className: DataLayerClassName.productTile,
                        }}
                        wishlist={{
                            onClick: () => {
                                // FIXME: should handle PRODUCT_SET case
                                if (shouldAddToWishlist) {
                                    updateWishlist({
                                        parentId: product.id,
                                        productId: product.sku,
                                        productName: product.name,
                                    });
                                } else {
                                    addIfAbsentAndOpen(
                                        WISHLIST_MODAL_ID,
                                        SideMenuModal({
                                            side: Position.None,
                                            map: {
                                                default: <WishlistModal productId={product.sku} locale={locale} />,
                                            },
                                            className: ModalCommonClasses,
                                        }),
                                    );
                                }
                            },
                            isAdded: isProductInWishlist(product.sku),
                            ariaLabel: wishlistBtnAriaLabel,
                        }}
                        legalMentionsLabel={product.legalMentionsLabel}
                    />
                );
            })}
        </GlowCarousel>
    );
};
